// components/create/CustomFieldModal.js
//
// "Add Custom Field" — the thing that did nothing.
//
// BasicSection had `showCustomFieldModal`, `currentCustomField`,
// `handleAddCustomField`, `handleEditCustomField` and `handleSaveCustomField`,
// all correct, and no modal anywhere in the render. Pressing the button set a
// piece of state nothing read, so the section could list custom fields, edit
// them and delete them, but there was no way to make one — which meant the
// whole block was dead on this site. (The build has been saying so: three
// "assigned a value but never used" warnings, in among two hundred others.)
//
// The shape is the app's, exactly:
//
//   { id, type, icon, label, value, link }
//
// `icon` is an Ionicon name, derived from the type rather than chosen, so the
// two surfaces draw the same glyph for "Dress Code". The invitation page reads
// `label` and `value`; `link` is what makes the value tappable where it is a
// URL.

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import '../../styles/CustomFieldModal.scss';
import Icon from '../Icon';
import Portal from '../Portal';

/** The app's CUSTOM_FIELD_TYPES, unchanged. */
export const CUSTOM_FIELD_TYPES = [
  { id: 'link', label: 'Website Link', icon: 'link-outline' },
  { id: 'dressCode', label: 'Dress Code', icon: 'shirt-outline' },
  { id: 'food', label: 'Food Information', icon: 'restaurant-outline' },
  { id: 'accommodation', label: 'Accommodation', icon: 'bed-outline' },
  { id: 'parking', label: 'Parking Info', icon: 'car-outline' },
  { id: 'contact', label: 'Contact Information', icon: 'call-outline' },
];

const iconForType = (type) => CUSTOM_FIELD_TYPES.find((f) => f.id === type)?.icon;

/** What the label box should say before anyone types, per type. */
const LABEL_HINTS = {
  link: 'Registry',
  dressCode: 'Dress code',
  food: 'Food',
  accommodation: 'Where to stay',
  parking: 'Parking',
  contact: 'Call us',
};

const VALUE_HINTS = {
  link: 'See the list',
  dressCode: 'Black tie, or whatever makes you happy',
  food: 'Dinner and drinks from 8',
  accommodation: 'Rooms held at the Taj until the 3rd',
  parking: 'Free parking behind the hall',
  contact: '+91 98765 43210',
};

export default function CustomFieldModal({ field, onSave, onClose }) {
  const editing = !!field;

  const [type, setType] = useState(field?.type || '');
  const [label, setLabel] = useState(field?.label || '');
  const [value, setValue] = useState(field?.value || '');
  const [link, setLink] = useState(field?.link || '');
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const chooseType = (nextType) => {
    setType(nextType);
    setError('');
    // Prefill the label from the type, since it is right most of the time and
    // still editable. Only when it is empty or still the previous type's
    // suggestion — never over something the host typed.
    const previousHint = LABEL_HINTS[type];
    if (!label.trim() || label === previousHint) {
      setLabel(LABEL_HINTS[nextType] || '');
    }
  };

  const save = () => {
    if (!type) { setError('Pick what kind of detail this is.'); return; }
    if (!label.trim()) { setError('Give it a label — that is the heading guests see.'); return; }
    if (!value.trim()) { setError('Add the detail itself.'); return; }

    onSave({
      // An existing field keeps its id, so editing replaces rather than
      // appends — which is what handleSaveCustomField's findIndex expects.
      id: field?.id || Date.now().toString(),
      type,
      icon: iconForType(type),
      label: label.trim(),
      value: value.trim(),
      link: link.trim(),
    });
    onClose();
  };

  return (
    <Portal>
    <div className="cfield-scrim" onClick={onClose}>
      <div
        className="cfield-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? 'Edit detail' : 'Add a detail'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cfield-head">
          <h2>{editing ? 'Edit detail' : 'Add a detail'}</h2>
          <button type="button" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        <div className="cfield-body">
          <fieldset className="cfield-types">
            <legend>What kind of detail?</legend>
            <div className="cfield-type-grid">
              {CUSTOM_FIELD_TYPES.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`cfield-type ${type === option.id ? 'is-active' : ''}`}
                  aria-pressed={type === option.id}
                  onClick={() => chooseType(option.id)}
                >
                  <Icon name={option.icon} size={19} />
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <div className="cfield-group">
            <label htmlFor="cfield-label">Label</label>
            <input
              id="cfield-label"
              type="text"
              maxLength={40}
              value={label}
              placeholder={LABEL_HINTS[type] || 'Dress code'}
              onChange={(e) => { setLabel(e.target.value); setError(''); }}
            />
          </div>

          <div className="cfield-group">
            <label htmlFor="cfield-value">Detail</label>
            <textarea
              id="cfield-value"
              rows={3}
              maxLength={300}
              value={value}
              placeholder={VALUE_HINTS[type] || 'What guests need to know'}
              onChange={(e) => { setValue(e.target.value); setError(''); }}
            />
          </div>

          <div className="cfield-group">
            <label htmlFor="cfield-link">Link (optional)</label>
            <input
              id="cfield-link"
              type="url"
              inputMode="url"
              value={link}
              placeholder="https://…"
              onChange={(e) => setLink(e.target.value)}
            />
            <small>Makes the detail tappable — a registry, a map, a booking page.</small>
          </div>

          {error && <p className="cfield-error">{error}</p>}
        </div>

        <div className="cfield-actions">
          <button type="button" className="cfield-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="cfield-btn cfield-btn--primary" onClick={save}>
            {editing ? 'Save' : 'Add detail'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
